import React, { FunctionComponent, ReactNode, useEffect, useState } from 'react'
import { Button, Heading, Modal, Text } from '@afterpaytouch/core'
import { useCheckoutSizes } from '../../utils/responsive'
import styles from './style.module.css'
import { useTranslation } from 'next-i18next'
import {
  selectPurchaseMethod,
  setCheckoutStatus,
  setSelectedPaymentType,
  useAppDispatch,
  useAppropriatePaymentMethod,
  useAppRouter,
  useCLRiskDeclineError,
  useCountry,
  usePaymentOptionsError,
} from '../../state'
import { Country, PaymentType } from '@afterpay/types'
import { checkoutErrors, CheckoutStatus, TerminalErrorCode } from '../../model'
import { Route } from '../../config/router'
import { FacilityContext } from '../../state/consumerLending/types'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { TrackingEvent } from '../../model/monitoring'

interface ConsumerLendingErrorModalProps {
  isError?: boolean
}

export const ConsumerLendingErrorModal: FunctionComponent<ConsumerLendingErrorModalProps> = ({ isError = false }) => {
  const { t } = useTranslation()
  const { headingSize, textSize } = useCheckoutSizes()
  const paymentMethod = useAppropriatePaymentMethod()
  const { isError: isRiskError, error, context } = useCLRiskDeclineError()
  const paymentOptionsError = usePaymentOptionsError()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const country = useCountry()
  const dispatch = useAppDispatch()
  const [showModal, setShowModal] = useState(false)
  const testNameSpace = 'consumer-lending-error'
  const router = useAppRouter()
  const errorCode = error?.errorCode ?? paymentOptionsError?.errorCode
  const errorKey = checkoutErrors[errorCode as string] ?? TerminalErrorCode.Default
  const maximumOrderValue = paymentOptionsError?.extra?.maximumOrderValue

  const setPaymentTypeToPbi = (): void => {
    dispatch(setSelectedPaymentType(PaymentType.PBI))
    if (paymentMethod !== null) {
      dispatch(selectPurchaseMethod(paymentMethod))
    } else {
      dispatch(setCheckoutStatus(CheckoutStatus.LackOfEligiblePaymentMethod))
    }
  }

  useEffect(() => {
    if (errorCode) {
      logEvent(TrackingEvent.VIEWED_NON_TERMINAL_ERROR, { errorCode })
    }
  }, [errorCode])

  const getHeading = (): ReactNode => {
    const contextKey = context !== undefined && errorKey !== TerminalErrorCode.Default ? `:${context}` : ''
    return (
      <div className={styles.container}>
        <Heading size={headingSize}>{t(`consumerLending:declines:${errorKey}${contextKey}:heading`)}</Heading>
      </div>
    )
  }

  const getSubheading = (): ReactNode => {
    return errorKey === 'amountTooHigh' && context === FacilityContext.FacilityCreation && maximumOrderValue !== undefined ? (
      <>
        <div className={styles.underlineContainer}>
          <Text size='XXXXL' bold>
            {maximumOrderValue?.split('.')[0]}
          </Text>
          <div className={styles.underline}></div>
        </div>
        <div className={styles.textContainer}>
          <Text bold>{t(`consumerLending:declines:${errorKey}:${context}:subheading`)}</Text>
        </div>
      </>
    ) : null
  }

  const getErrorMessage = (): ReactNode => {
    const showTagline =
      context === FacilityContext.IncomeAssessment || !(context === FacilityContext.FacilityCreation && errorKey === TerminalErrorCode.AmountTooHigh)
    const tagLineKey = errorKey === TerminalErrorCode.ExcessiveLateFeeAmount ? 'altTagLine' : 'payInFourTagline'
    return errorKey !== TerminalErrorCode.Default ? (
      <>
        <Text>{t(`consumerLending:declines:${errorKey}:${context}:p1`)}</Text>
        {showTagline && (
          <div className={styles.container}>
            <Text bold>{t(`consumerLending:declines:${tagLineKey}`)}</Text>
          </div>
        )}
      </>
    ) : (
      <Text>{t(`consumerLending:declines:${errorKey}:p1`)}</Text>
    )
  }

  const closeModal = (): void => {
    setShowModal(false)
  }

  const handleContinueClick = (): void => {
    logEvent(TrackingEvent.PRESSED_NON_TERMINAL_ERROR_PAGE_CONTINUE_WITH_PAY_IN_4_BUTTON, { errorCode })

    if (isRiskError || paymentOptionsError != null) {
      closeModal()
    } else {
      setPaymentTypeToPbi()
      router.push(Route.SUMMARY)
    }
  }
  useEffect(() => {
    if (isError && country === Country.AU) {
      setShowModal(true)
    }
  }, [isError, country])

  return (
    <Modal show={showModal} showCloseButton onClose={closeModal} onBackdropClick={closeModal} testNameSpace={testNameSpace}>
      <Modal.Header>
        {getHeading()}
        {getSubheading()}
      </Modal.Header>
      <Modal.Content>
        {getErrorMessage()}
        <div className={styles.container}>
          <Button onClick={handleContinueClick} padding='Fluid' testNameSpace={testNameSpace}>
            <Heading size={textSize} testNameSpace={testNameSpace}>
              {t('consumerLending:approvedCredit:payIn4Button')}
            </Heading>
          </Button>
        </div>
      </Modal.Content>
    </Modal>
  )
}
